import axios from 'axios';
import config from '../config';
import { authAPI } from '../api';

const CALL_REQUEST_TIMEOUT_MS = 10000;
const CALL_HISTORY_TIMEOUT_MS = 8000;
const CALL_RESET_TIMEOUT_MS = 3000;
export const CALL_WS_OPEN_TIMEOUT_MS = 15000;
const DUPLICATE_WS_CLOSE_CODE = 4001;
const RATE_LIMIT_WS_CLOSE_CODE = 4002;
const MIN_WS_CREATE_INTERVAL_MS = 3000;
const MAX_WS_CREATES_PER_CALL = 3;

let callSocketLockOwner = 'local';

export const setCallSocketLockOwner = (userId) => {
  callSocketLockOwner = userId ? String(userId) : 'local';
};

const getCallWsBase = () => {
  if (typeof window !== 'undefined' && window.location && window.location.host) {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.host}/api/v1/ws`;
  }
  return config.WS_BASE_URL || 'ws://localhost/api/v1/ws';
};

const withCallAuth = async (requestConfig, timeout = CALL_REQUEST_TIMEOUT_MS) => {
  const tokenResponse = await authAPI.getToken();
  const token = tokenResponse.data.token;
  return axios({
    timeout,
    ...requestConfig,
    headers: {
      ...(requestConfig.headers || {}),
      Authorization: `Bearer ${token}`,
    },
  });
};

export const waitForWebSocketOpen = (ws, timeoutMs = CALL_WS_OPEN_TIMEOUT_MS) => new Promise(
  (resolve, reject) => {
    if (ws.readyState === WebSocket.OPEN) {
      resolve();
      return;
    }
    if (ws.readyState === WebSocket.CLOSED || ws.readyState === WebSocket.CLOSING) {
      reject(new Error('call websocket closed before open'));
      return;
    }

    const cleanup = () => {
      window.clearTimeout(timer);
      ws.removeEventListener('open', onOpen);
      ws.removeEventListener('error', onError);
      ws.removeEventListener('close', onClose);
    };

    const timer = window.setTimeout(() => {
      cleanup();
      try {
        ws.close();
      } catch (_) {
        // ignore
      }
      reject(new Error('call websocket connection timed out'));
    }, timeoutMs);

    const onOpen = () => {
      cleanup();
      resolve();
    };
    const onError = () => {
      cleanup();
      reject(new Error('call websocket failed'));
    };
    const onClose = () => {
      cleanup();
      reject(new Error('call websocket closed before open'));
    };

    ws.addEventListener('open', onOpen);
    ws.addEventListener('error', onError);
    ws.addEventListener('close', onClose);
  },
);

const closeWebSocketQuietly = (ws) => {
  if (!ws) {
    return;
  }
  try {
    ws.onmessage = null;
    ws.onclose = null;
    ws.onerror = null;
    if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
      ws.close();
    }
  } catch (_) {
    // ignore
  }
};

const isSocketOpen = (ws) => ws?.readyState === WebSocket.OPEN;
const isSocketConnecting = (ws) => ws?.readyState === WebSocket.CONNECTING;
const isSocketLive = (ws) => isSocketOpen(ws) || isSocketConnecting(ws);

/** Survives HMR: one signaling socket registry per tab. */
const getCallSocketRegistry = () => {
  if (typeof window === 'undefined') {
    return new Map();
  }
  if (!window.__lynxCallSocketRegistry) {
    window.__lynxCallSocketRegistry = new Map();
  }
  return window.__lynxCallSocketRegistry;
};

const getRegistryEntry = (callId) => {
  const key = String(callId);
  const registry = getCallSocketRegistry();
  let entry = registry.get(key);
  if (!entry) {
    entry = {
      callId: key,
      ws: null,
      inflightWs: null,
      connectTask: null,
      closeHandler: null,
      generation: 0,
      hadStableOpen: false,
      blockedUntil: 0,
    };
    registry.set(key, entry);
  }
  return entry;
};

const dispatchCallSignalMessage = (event) => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    const payload = JSON.parse(event.data);
    window.__lynxCallSignalHandler?.(payload);
  } catch (_) {
    // ignore malformed payloads
  }
};

const attachWebSocketHandlers = (entry, ws) => {
  ws.onmessage = (event) => {
    if (entry.ws !== ws) {
      return;
    }
    dispatchCallSignalMessage(event);
  };

  ws.onclose = (event) => {
    if (entry.ws !== ws && entry.inflightWs !== ws) {
      return;
    }
    if (entry.inflightWs === ws) {
      entry.inflightWs = null;
    }
    if (entry.ws !== ws) {
      return;
    }
    entry.ws = null;
    console.info('[call] ws closed', {
      callId: entry.callId,
      code: event.code,
      reason: event.reason || 'closed',
    });
    if (event.code === DUPLICATE_WS_CLOSE_CODE || event.code === RATE_LIMIT_WS_CLOSE_CODE) {
      if (event.code === RATE_LIMIT_WS_CLOSE_CODE) {
        entry.blockedUntil = Date.now() + 15000;
      }
      return;
    }
    entry.closeHandler?.(event);
  };
};

const getWsCreateStats = () => {
  if (typeof window === 'undefined') {
    return new Map();
  }
  if (!window.__lynxCallWsCreateStats) {
    window.__lynxCallWsCreateStats = new Map();
  }
  return window.__lynxCallWsCreateStats;
};

export const resetCallSocketGuard = (callId) => {
  const key = String(callId);
  const entry = getCallSocketRegistry().get(key);
  if (entry) {
    entry.blockedUntil = 0;
  }
  getWsCreateStats().delete(key);
};

const createCallWebSocketRaw = async (callId, entry) => {
  const key = String(callId);
  const stats = getWsCreateStats();
  const now = Date.now();
  const entryStats = stats.get(key) || { count: 0, lastAt: 0 };
  if (entryStats.count >= MAX_WS_CREATES_PER_CALL) {
    entry.blockedUntil = Date.now() + 30000;
    throw new Error('call websocket create limit reached');
  }
  if (now - entryStats.lastAt < MIN_WS_CREATE_INTERVAL_MS) {
    await new Promise((resolve) => {
      window.setTimeout(resolve, MIN_WS_CREATE_INTERVAL_MS - (now - entryStats.lastAt));
    });
  }
  entryStats.count += 1;
  entryStats.lastAt = Date.now();
  stats.set(key, entryStats);

  // Prefer cookie auth (same-origin). Avoid subprotocol JWT — LockManager/path bugs aside,
  // cookie WS matches system/chat and does not depend on getToken before upgrade.
  const wsUrl = `${getCallWsBase()}/call/${callId}`;
  console.info('[call] ws create', {
    callId: String(callId),
    url: wsUrl,
    attempt: entryStats.count,
  });
  if (process.env.NODE_ENV === 'development') {
    console.debug('[call] ws create stack', new Error('ws create').stack?.split('\n').slice(1, 4).join(' | '));
  }
  const ws = new WebSocket(wsUrl);
  entry.inflightWs = ws;
  return ws;
};

const waitForLiveSocket = async (entry) => {
  if (isSocketOpen(entry.ws)) {
    return entry.ws;
  }
  if (isSocketConnecting(entry.ws)) {
    await waitForWebSocketOpen(entry.ws);
    if (isSocketOpen(entry.ws)) {
      return entry.ws;
    }
  }
  if (isSocketLive(entry.inflightWs)) {
    await waitForWebSocketOpen(entry.inflightWs);
    if (isSocketOpen(entry.inflightWs)) {
      if (!entry.ws) {
        entry.ws = entry.inflightWs;
        attachWebSocketHandlers(entry, entry.inflightWs);
      }
      return entry.inflightWs;
    }
  }
  return null;
};

const connectCallSignalingSocket = async (entry, callId) => {
  const existing = await waitForLiveSocket(entry);
  if (existing) {
    return existing;
  }

  const generation = entry.generation;
  const ws = await createCallWebSocketRaw(callId, entry);

  if (entry.generation !== generation) {
    closeWebSocketQuietly(ws);
    entry.inflightWs = null;
    throw new Error('call websocket superseded');
  }

  const liveAfterCreate = await waitForLiveSocket(entry);
  if (liveAfterCreate && liveAfterCreate !== ws) {
    closeWebSocketQuietly(ws);
    if (entry.inflightWs === ws) {
      entry.inflightWs = null;
    }
    return liveAfterCreate;
  }

  if (!entry.ws) {
    entry.ws = ws;
    attachWebSocketHandlers(entry, ws);
  }

  if (ws.readyState !== WebSocket.OPEN) {
    await waitForWebSocketOpen(ws);
  }

  if (entry.generation !== generation || entry.ws !== ws) {
    closeWebSocketQuietly(ws);
    if (entry.inflightWs === ws) {
      entry.inflightWs = null;
    }
    throw new Error('call websocket superseded');
  }

  entry.inflightWs = null;
  entry.hadStableOpen = true;
  console.info('[call] ws open', { callId: entry.callId });
  return ws;
};

const acquireCallSignalingSocketInner = async (callId, { onClose } = {}) => {
  const entry = getRegistryEntry(callId);

  if (onClose) {
    entry.closeHandler = onClose;
  }

  if (entry.blockedUntil && Date.now() < entry.blockedUntil) {
    throw new Error('call websocket temporarily blocked after reconnect storm');
  }

  if (isSocketOpen(entry.ws)) {
    return entry.ws;
  }

  if (entry.connectTask) {
    return entry.connectTask;
  }

  entry.connectTask = connectCallSignalingSocket(entry, callId)
    .finally(() => {
      entry.connectTask = null;
    });

  try {
    return await entry.connectTask;
  } catch (error) {
    if (!isSocketOpen(entry.ws)) {
      entry.ws = null;
      entry.inflightWs = null;
    }
    throw error;
  }
};

const withCallSocketLock = async (callId, fn) => {
  const lockName = `lynx-call-ws-${callId}-${callSocketLockOwner}`;
  if (typeof navigator !== 'undefined' && navigator.locks?.request) {
    // Web Locks API: request(name, callback) | request(name, options, callback)
    return navigator.locks.request(lockName, { mode: 'exclusive' }, fn);
  }
  return fn();
};

export const acquireCallSignalingSocket = async (callId, handlers = {}) => (
  withCallSocketLock(String(callId), () => acquireCallSignalingSocketInner(callId, handlers))
);

export const releaseCallSignalingSocket = (callId) => {
  const key = String(callId);
  const registry = getCallSocketRegistry();
  const entry = registry.get(key);
  if (!entry) {
    return;
  }
  entry.generation += 1;
  entry.connectTask = null;
  entry.closeHandler = null;
  closeWebSocketQuietly(entry.inflightWs);
  closeWebSocketQuietly(entry.ws);
  entry.inflightWs = null;
  entry.ws = null;
  registry.delete(key);
  getWsCreateStats().delete(key);
};

export const getCallSignalingSocket = (callId) => {
  const entry = getCallSocketRegistry().get(String(callId));
  return isSocketOpen(entry?.ws) ? entry.ws : null;
};

export const hadStableCallSignalingSocket = (callId) => {
  const entry = getCallSocketRegistry().get(String(callId));
  return Boolean(entry?.hadStableOpen);
};

export const isDuplicateCallSocketClose = (event) => (
  event?.code === DUPLICATE_WS_CLOSE_CODE
);

export const isRateLimitedCallSocketClose = (event) => (
  event?.code === RATE_LIMIT_WS_CLOSE_CODE
);

export const callAPI = {
  create: (chatId) => withCallAuth({
    method: 'post',
    url: `${config.API_BASE_URL}/api/v1/calls`,
    data: { chat_id: chatId },
  }),
  accept: (callId) => withCallAuth({
    method: 'post',
    url: `${config.API_BASE_URL}/api/v1/calls/${callId}/accept`,
  }),
  reject: (callId) => withCallAuth({
    method: 'post',
    url: `${config.API_BASE_URL}/api/v1/calls/${callId}/reject`,
  }),
  end: (callId) => withCallAuth({
    method: 'post',
    url: `${config.API_BASE_URL}/api/v1/calls/${callId}/end`,
  }),
  reset: () => withCallAuth({
    method: 'post',
    url: `${config.API_BASE_URL}/api/v1/calls/reset`,
  }, CALL_RESET_TIMEOUT_MS),
  getIceServers: (callId) => withCallAuth({
    method: 'get',
    url: `${config.API_BASE_URL}/api/v1/calls/${callId}/ice-servers`,
  }),
  createRoom: (chatId) => withCallAuth({
    method: 'post',
    url: `${config.API_BASE_URL}/api/v1/calls/rooms`,
    data: { chat_id: chatId },
  }),
  getActiveRoom: (chatId) => withCallAuth({
    method: 'get',
    url: `${config.API_BASE_URL}/api/v1/calls/rooms/active`,
    params: { chat_id: chatId },
  }),
  joinRoom: (roomId) => withCallAuth({
    method: 'post',
    url: `${config.API_BASE_URL}/api/v1/calls/rooms/${roomId}/join`,
  }),
  leaveRoom: (roomId) => withCallAuth({
    method: 'post',
    url: `${config.API_BASE_URL}/api/v1/calls/rooms/${roomId}/leave`,
  }),
  terminateRoom: (roomId) => withCallAuth({
    method: 'post',
    url: `${config.API_BASE_URL}/api/v1/calls/rooms/${roomId}/terminate`,
  }),
  getHistory: ({ chatId, limit = 50, offset = 0, signal } = {}) => withCallAuth({
    method: 'get',
    url: `${config.API_BASE_URL}/api/v1/calls/history`,
    params: {
      ...(chatId ? { chat_id: chatId } : {}),
      limit,
      offset,
    },
    signal,
  }, CALL_HISTORY_TIMEOUT_MS),
};
